import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/adminApi";
import { SECTIONS } from "@/components/admin/schemas";
import { SectionForm } from "@/components/admin/Fields";

export const ContentEditor = () => {
  const [content, setContent] = useState(null);
  const [active, setActive] = useState("hero");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi
      .get("/content")
      .then((r) => setContent(r.data))
      .catch(() => toast.error("Failed to load content"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.put("/content", content);
      setContent(data);
      toast.success("Content saved — the site is updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!content)
    return (
      <div className="flex items-center gap-2 text-[#5B6F64] py-12">
        <Loader2 size={18} className="animate-spin" /> Loading content…
      </div>
    );

  const section = SECTIONS.find((s) => s.id === active);

  return (
    <div data-testid="content-editor" className="grid lg:grid-cols-[200px_1fr] gap-8">
      <nav className="flex lg:flex-col flex-wrap gap-1">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            data-testid={`content-tab-${s.id}`}
            onClick={() => setActive(s.id)}
            className={`text-left text-sm uppercase tracking-wider px-4 py-2.5 transition-colors ${
              active === s.id
                ? "bg-[#122A1D] text-[#E3C97A]"
                : "text-[#5B6F64] hover:bg-[#122A1D]/5"
            }`}
          >
            {s.label}
          </button>
        ))}
      </nav>

      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-3xl text-[#122A1D]">{section.label}</h2>
          <button
            onClick={save}
            disabled={saving}
            data-testid="content-save-button"
            className="bg-[#C8A24A] text-[#0B1F16] hover:bg-[#B8923D] disabled:opacity-60 font-semibold transition-colors px-6 py-2.5 uppercase tracking-wider text-xs flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save changes
          </button>
        </div>
        <SectionForm
          sectionId={section.id}
          schema={section.schema}
          data={content[section.id]}
          onChange={(d) => setContent({ ...content, [section.id]: d })}
        />
      </div>
    </div>
  );
};
